/**
 * Sticky Add to Cart Component
 * Muestra un botón flotante de agregar al carrito en móvil
 */

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.showEverywhere = this.dataset.showEverywhere === 'true';
    this.scrollThreshold = parseInt(this.dataset.scrollThreshold) || 20;
    this.buttonAction = this.dataset.buttonAction || 'go_to_checkout';
    
    this.quantityInput = this.querySelector('.sticky-atc__quantity-input');
    this.quantityMinus = this.querySelector('.sticky-atc__quantity-minus');
    this.quantityPlus = this.querySelector('.sticky-atc__quantity-plus');
    this.addButton = this.querySelector('.sticky-atc__add-button');
    this.variantSelects = this.querySelectorAll('.sticky-atc__variant-select');
    this.successNotification = this.querySelector('.sticky-atc__success');
    
    this.currentVariantId = null;
    this.isVisible = false;
    this.isLoading = false;
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupScrollListener();
    this.updateVariant();
    this.syncWithMainForm();
    
    // Solo mostrar en páginas de producto o si showEverywhere está habilitado
    if (!this.showEverywhere && !this.isProductPage()) {
      this.style.display = 'none';
      return;
    }
  }

  bindEvents() {
    // Cantidad
    this.quantityMinus?.addEventListener('click', () => this.updateQuantity(-1));
    this.quantityPlus?.addEventListener('click', () => this.updateQuantity(1));
    
    // Variantes
    this.variantSelects.forEach(select => {
      select.addEventListener('change', () => this.updateVariant());
    });
    
    // Agregar al carrito
    this.addButton?.addEventListener('click', () => this.addToCart());
    
    // Escuchar cambios en el producto principal si existe
    this.listenToMainProductForm();
  }

  setupScrollListener() {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    this.updateVisibility(); // Check initial state
  }

  updateVisibility() {
    const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
    const shouldShow = scrollPercent > this.scrollThreshold;
    
    if (shouldShow && !this.isVisible) {
      this.show();
    } else if (!shouldShow && this.isVisible) {
      this.hide();
    }
  }

  show() {
    this.isVisible = true;
    this.classList.add('is-visible');
  }

  hide() {
    this.isVisible = false;
    this.classList.remove('is-visible');
  }

  updateQuantity(change) {
    if (!this.quantityInput) return;
    
    const currentValue = parseInt(this.quantityInput.value) || 1;
    const newValue = Math.max(1, currentValue + change);
    
    this.quantityInput.value = newValue;
    
    // Update button states
    if (this.quantityMinus) {
      this.quantityMinus.disabled = newValue <= 1;
    }
  }

  updateVariant() {
    if (!this.variantSelects.length) return;
    
    // Crear array de opciones seleccionadas
    const selectedOptions = Array.from(this.variantSelects).map(select => select.value);
    
    // Encontrar la variante que coincida (esto requeriría datos del producto)
    this.findMatchingVariant(selectedOptions);
  }

  findMatchingVariant(selectedOptions) {
    // En una implementación completa, esto buscaría en window.productVariants
    // Por ahora, asumimos que hay un producto principal
    if (window.product && window.product.variants) {
      const variant = window.product.variants.find(v => {
        return selectedOptions.every((option, index) => {
          return v.options[index] === option;
        });
      });
      
      if (variant) {
        this.currentVariantId = variant.id;
        this.updateAddButton(variant);
      }
    }
  }

  updateAddButton(variant) {
    if (!this.addButton) return;
    
    const buttonText = this.addButton.querySelector('.sticky-atc__button-text');
    
    if (variant && variant.available) {
      this.addButton.disabled = false;
      if (buttonText) {
        buttonText.textContent = this.addButton.dataset.addText || 'Agregar al carrito';
      }
    } else {
      this.addButton.disabled = true;
      if (buttonText) {
        buttonText.textContent = 'Agotado';
      }
    }
  }

  getCurrentVariantId() {
    // Try to get variant from Posturax selector first
    const posturaxSelector = document.querySelector('posturax-variant-selector');
    if (posturaxSelector && posturaxSelector.currentVariant) {
      console.log('Sticky ATC - Got variant from Posturax selector:', posturaxSelector.currentVariant.id);
      return posturaxSelector.currentVariant.id;
    }
    
    // Try to get variant from main buy buttons form (most reliable)
    const buyButtonsForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (buyButtonsForm) {
      const variantInput = buyButtonsForm.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        console.log('Sticky ATC - Got variant from buy buttons form:', variantInput.value);
        return variantInput.value;
      }
    }
    
    // Try to get variant from main product form
    const mainProductForm = document.querySelector('[data-product-form]');
    if (mainProductForm) {
      const variantInput = mainProductForm.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        console.log('Sticky ATC - Got variant from main form:', variantInput.value);
        return variantInput.value;
      }
    }
    
    // Try to get from any product form on the page
    const anyProductForm = document.querySelector('form[action*="/cart/add"]');
    if (anyProductForm) {
      const variantInput = anyProductForm.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        console.log('Sticky ATC - Got variant from any form:', variantInput.value);
        return variantInput.value;
      }
    }
    
    // Try to get from bundle form
    const bundleForm = document.querySelector('#posturax-bundle-form');
    if (bundleForm) {
      const variantInput = bundleForm.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        console.log('Sticky ATC - Got variant from bundle form:', variantInput.value);
        return variantInput.value;
      }
    }
    
    // Fallback to stored variant or product ID
    console.log('Sticky ATC - Using fallback variant:', this.currentVariantId || this.productId);
    return this.currentVariantId || this.productId;
  }

  async addToCart() {
    if (this.isLoading) return;
    
    const quantity = parseInt(this.quantityInput?.value) || 1;
    
    // Try to get variant from Posturax selector first, then fallback to current variant
    let variantId = this.getCurrentVariantId();
    
    console.log('Sticky ATC - Button action:', this.buttonAction);
    console.log('Sticky ATC - Variant ID:', variantId);
    console.log('Sticky ATC - Product ID:', this.productId);
    
    if (!variantId) {
      console.error('No variant ID found');
      return;
    }
    
    this.setLoading(true);
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Update cart icon
        this.updateCartIcon();
        
        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { item: data }
        }));
        
        // Handle action based on configuration
        console.log('Sticky ATC - Action to take:', this.buttonAction);
        if (this.buttonAction === 'go_to_checkout') {
          // Redirect to checkout after successful add
          console.log('Sticky ATC - Redirecting to checkout...');
          setTimeout(() => {
            window.location.href = '/checkout';
          }, 500);
        } else {
          // Show success notification for add to cart
          console.log('Sticky ATC - Showing success notification');
          this.showSuccess();
        }
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to add to cart:', response.status, errorData);
        throw new Error(`Failed to add to cart: ${errorData.description || response.statusText}`);
      }
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError();
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    this.isLoading = loading;
    
    if (this.addButton) {
      this.addButton.classList.toggle('is-loading', loading);
      this.addButton.disabled = loading;
    }
  }

  showSuccess() {
    if (!this.successNotification) return;
    
    this.successNotification.classList.add('is-visible');
    
    setTimeout(() => {
      this.successNotification.classList.remove('is-visible');
    }, 2000);
  }

  showError() {
    // Mostrar notificación de error
    console.error('Error al agregar al carrito');
  }

  updateCartIcon() {
    // Actualizar el icono del carrito si existe
    const cartIcons = document.querySelectorAll('[data-cart-count]');
    
    cartIcons.forEach(icon => {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          icon.textContent = cart.item_count;
          icon.style.display = cart.item_count > 0 ? 'block' : 'none';
        });
    });
  }

  listenToMainProductForm() {
    // Escuchar cambios en el formulario principal del producto
    const mainProductForm = document.querySelector('[data-product-form]');
    
    if (mainProductForm) {
      const variantInputs = mainProductForm.querySelectorAll('[name^="option"]');
      
      variantInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.syncWithMainForm();
        });
      });
    }
    
    // También escuchar cambios del Posturax Variant Selector
    const posturaxSelector = document.querySelector('posturax-variant-selector');
    if (posturaxSelector) {
      posturaxSelector.addEventListener('variant:changed', () => {
        this.syncWithPosturaxSelector();
      });
      
      // Also listen for custom events
      document.addEventListener('posturax:variant:changed', (event) => {
        if (event.detail && event.detail.variant) {
          this.currentVariantId = event.detail.variant.id;
        }
      });
    }
  }

  syncWithMainForm() {
    // Try to get variant from buy buttons form first
    const buyButtonsForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (buyButtonsForm) {
      const variantInput = buyButtonsForm.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        this.currentVariantId = variantInput.value;
        console.log('Sticky ATC - Synced variant from buy buttons form:', variantInput.value);
      }
    }
    
    // Also try main product form
    const mainProductForm = document.querySelector('[data-product-form]');
    if (mainProductForm) {
      const variantInput = mainProductForm.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        this.currentVariantId = variantInput.value;
        console.log('Sticky ATC - Synced variant from main form:', variantInput.value);
      }
      
      // Sincronizar variantes seleccionadas
      const formData = new FormData(mainProductForm);
      
      this.variantSelects.forEach((select, index) => {
        const optionName = `option${index + 1}`;
        const selectedValue = formData.get(optionName);
        
        if (selectedValue && select.value !== selectedValue) {
          select.value = selectedValue;
        }
      });
    }
    
    this.updateVariant();
  }

  syncWithPosturaxSelector() {
    const posturaxSelector = document.querySelector('posturax-variant-selector');
    if (!posturaxSelector) return;
    
    const selectedVariant = posturaxSelector.getSelectedVariant();
    if (selectedVariant) {
      this.currentVariantId = selectedVariant.id;
      this.updateAddButton(selectedVariant);
      
      // Update product info
      const titleElement = this.querySelector('.sticky-atc__product-title');
      const priceElement = this.querySelector('.sticky-atc__price');
      const imageElement = this.querySelector('.sticky-atc__thumbnail');
      
      if (priceElement) {
        priceElement.textContent = this.formatMoney(selectedVariant.price);
      }
      
      if (imageElement && selectedVariant.featured_media) {
        imageElement.src = selectedVariant.featured_media.preview_image.src;
        imageElement.alt = selectedVariant.name;
      }
    }
  }

  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount);
  }

  isProductPage() {
    return document.body.classList.contains('template-product') || 
           window.location.pathname.includes('/products/');
  }
}

// Registrar el custom element
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}

// Función helper para inicializar en páginas sin producto específico
window.initStickyAddToCart = function(productData) {
  const stickyAtc = document.querySelector('sticky-add-to-cart');
  
  if (stickyAtc && productData) {
    stickyAtc.productId = productData.id;
    stickyAtc.currentVariantId = productData.variants[0]?.id;
    
    // Actualizar información del producto
    const titleElement = stickyAtc.querySelector('.sticky-atc__product-title');
    const priceElement = stickyAtc.querySelector('.sticky-atc__price');
    const imageElement = stickyAtc.querySelector('.sticky-atc__thumbnail');
    
    if (titleElement) titleElement.textContent = productData.title;
    if (priceElement) priceElement.textContent = Shopify.formatMoney(productData.price);
    if (imageElement && productData.featured_image) {
      imageElement.src = productData.featured_image;
      imageElement.alt = productData.title;
    }
  }
};

export default StickyAddToCart;
